/**
 * Field definitions for the Data Entry Center forms.
 * Keys match FORM_CATALOG keys in /app/backend/routers/data_entry.py.
 * Each field: { name, label, type, required?, options?, defaultValue? }.
 */

export const FORM_FIELDS = {
  // ===== Finance =====
  income: [
    { name: 'source', label: 'Source', type: 'select', required: true,
      options: ['school_fees', 'donations', 'grants', 'chicken_sales', 'garden_produce',
        'masonry_sales', 'maize_sales', 'loan_interest', 'fundraising', 'sponsorship', 'training_fees'] },
    { name: 'amount', label: 'Amount (UGX)', type: 'number', required: true },
    { name: 'date', label: 'Date', type: 'date', required: true },
    { name: 'description', label: 'Description', type: 'text' },
  ],

  school_fees: [
    { name: 'student_first_name', label: 'Student First Name', type: 'text', required: true },
    { name: 'student_last_name', label: 'Student Last Name', type: 'text', required: true },
    { name: 'student_class', label: 'Class', type: 'text' },
    { name: 'parent_guardian_name', label: 'Parent / Guardian', type: 'text' },
    { name: 'amount', label: 'Amount Paid (UGX)', type: 'number', required: true },
    { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
    { name: 'date', label: 'Date', type: 'date', required: true },
  ],

  grant: [
    { name: 'grant_name', label: 'Grant Name', type: 'text', required: true },
    { name: 'date', label: 'Date Received', type: 'date', required: true },
    { name: 'currency', label: 'Currency', type: 'select', required: true,
      options: [
        { value: 'USD', label: 'US Dollars (USD)' },
        { value: 'EUR', label: 'Euros (EUR)' },
        { value: 'GBP', label: 'UK Pounds (GBP)' },
        { value: 'UGX', label: 'Uganda Shillings (UGX)' },
        { value: 'OTHER', label: 'Other' },
      ] },
    { name: 'original_amount', label: 'Original Amount', type: 'number', required: true },
    { name: 'exchange_rate', label: 'Exchange Rate (→ UGX)', type: 'number', required: true },
    { name: 'amount', label: 'Amount in UGX', type: 'number', required: true },
  ],

  chicken_sales: [
    { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
    { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
    { name: 'amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
    { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
    { name: 'date', label: 'Date', type: 'date', required: true },
  ],

  garden_produce: [
    { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
    { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
    { name: 'amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
    { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
    { name: 'date', label: 'Date', type: 'date', required: true },
  ],

  masonry_sales: [
    { name: 'sales_cycle', label: 'Sales Cycle', type: 'text', required: true },
    { name: 'items_sold', label: 'Number of Items Sold', type: 'number', required: true },
    { name: 'amount', label: 'Payment Amount (UGX)', type: 'number', required: true },
    { name: 'outstanding_balance', label: 'Outstanding Balance (UGX)', type: 'number' },
    { name: 'date', label: 'Date', type: 'date', required: true },
  ],

  expense: [
    { name: 'category', label: 'Subcategory (auto-grouped)', type: 'select', required: true,
      options: ['staff_salaries', 'benefits_taxes', 'health_insurance', 'career_development',
        'staff_food', 'students_food', 'school_farm', 'transport_travel', 'school_events', 'sponsorship',
        'rent_facilities', 'equipment', 'office_supplies', 'communications', 'professional_services',
        'fundraising_expenses', 'taxes_compliance', 'construction_project', 'sustainable_projects',
        'events', 'repairs'] },
    { name: 'amount', label: 'Amount (UGX)', type: 'number', required: true },
    { name: 'date', label: 'Date', type: 'date', required: true },
    { name: 'description', label: 'Description', type: 'text' },
  ],

  // ===== Personnel =====
  student: [
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'school', label: 'School', type: 'select', required: true,
      options: [{ value: 'suubi_primary', label: 'Suubi Primary' }, { value: 'suubi_secondary', label: 'Suubi Secondary' }] },
    { name: 'student_class', label: 'Class', type: 'text' },
    { name: 'gender', label: 'Gender', type: 'select',
      options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }] },
    { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
    { name: 'parent_names', label: 'Parent Names', type: 'text' },
    { name: 'parent_contact', label: 'Parent Contact', type: 'text' },
    { name: 'home_address', label: 'Address', type: 'text' },
  ],

  staff: [
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'select', required: true,
      options: ['administrator', 'teaching', 'non_teaching'] },
    { name: 'subject', label: 'Subject (if teaching)', type: 'text' },
    { name: 'title', label: 'Title / Position', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'home_address', label: 'Address', type: 'text' },
    { name: 'salary', label: 'Salary (UGX)', type: 'number' },
  ],

  catchup_student: [
    { name: 'student_name', label: 'Student Name', type: 'text', required: true },
    { name: 'class_grade', label: 'Class', type: 'text' },
    { name: 'class_teacher', label: 'Class Teacher', type: 'text' },
    { name: 'parent_guardian_name', label: 'Parent', type: 'text' },
    { name: 'parent_contact', label: 'Parent Phone', type: 'text' },
    { name: 'circumstances', label: 'Circumstances', type: 'textarea' },
    { name: 'focus_subjects', label: 'Focus Subjects', type: 'text' },
  ],

  // ===== Programs =====
  loan: [
    { name: 'borrower_name', label: 'Borrower Name', type: 'text', required: true },
    { name: 'borrower_phone', label: 'Phone', type: 'text' },
    { name: 'business_type', label: 'Business', type: 'text' },
    { name: 'loan_amount', label: 'Loan Amount (UGX)', type: 'number', required: true },
    { name: 'interest_rate', label: 'Interest %', type: 'number' },
    { name: 'duration_months', label: 'Term (months)', type: 'number' },
    { name: 'issue_date', label: 'Issue Date', type: 'date', required: true },
  ],

  sponsorship_child: [
    { name: 'name', label: 'Child Name', type: 'text', required: true },
    { name: 'grade', label: 'Class / Grade', type: 'text' },
    { name: 'school', label: 'School', type: 'text' },
    { name: 'sponsor_name', label: 'Sponsor', type: 'text' },
    { name: 'sponsor_contact', label: 'Sponsor Contact', type: 'text' },
    { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  ],

  maize_purchase: [
    { name: 'farmer_id', label: 'Farmer ID', type: 'text', required: true },
    { name: 'quantity_kg', label: 'Quantity (kg)', type: 'number', required: true },
    { name: 'price_per_kg', label: 'Price per kg (UGX)', type: 'number', required: true },
    { name: 'quality_grade', label: 'Grade', type: 'select',
      options: ['A', 'B', 'C'] },
    { name: 'date', label: 'Date', type: 'date', required: true },
    { name: 'notes', label: 'Notes', type: 'text' },
  ],

  club_member: [
    { name: 'club_id', label: 'Club ID', type: 'text', required: true },
    { name: 'student_name', label: 'Student Name', type: 'text', required: true },
    { name: 'role', label: 'Role', type: 'select',
      options: ['President', 'Vice President', 'Secretary', 'Treasurer', 'Member'] },
    { name: 'grade_class', label: 'Class', type: 'text' },
    { name: 'parent_name', label: 'Parent', type: 'text' },
    { name: 'parent_contact', label: 'Parent Phone', type: 'text' },
  ],

  // ===== Assets =====
  inventory: [
    { name: 'name', label: 'Item Name', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'text' },
    { name: 'quantity', label: 'Quantity', type: 'number', required: true },
    { name: 'unit', label: 'Unit', type: 'text' },
    { name: 'min_stock', label: 'Min Stock Level', type: 'number' },
    { name: 'location', label: 'Location', type: 'text' },
    { name: 'unit_cost', label: 'Unit Cost (UGX)', type: 'number' },
  ],

  maintenance: [
    { name: 'asset_id', label: 'Asset ID', type: 'text', required: true },
    { name: 'asset_name', label: 'Asset Name', type: 'text' },
    { name: 'maintenance_type', label: 'Type', type: 'select', required: true,
      options: ['preventive', 'corrective', 'inspection', 'other'] },
    { name: 'scheduled_date', label: 'Scheduled Date', type: 'date', required: true },
    { name: 'cost', label: 'Cost (UGX)', type: 'number' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],

  // ===== Operations =====
  requisition: [
    { name: 'requester_first_name', label: 'Requester First Name', type: 'text', required: true },
    { name: 'requester_last_name', label: 'Requester Last Name', type: 'text', required: true },
    { name: 'job_title', label: 'Job Title / Designation', type: 'text', required: true },
    { name: 'request_date', label: 'Date of Request', type: 'date', required: true },
    { name: 'budget_code', label: 'Budget Code / Cost Center', type: 'text', required: true },
    { name: 'budget_status', label: 'Budget Status', type: 'select', required: true,
      options: [
        { value: 'within_budget', label: 'Within Approved Budget' },
        { value: 'over_budget', label: 'Over Budget — needs review' },
        { value: 'pending_approval', label: 'Pending Budget Approval' },
        { value: 'unbudgeted', label: 'Unbudgeted Request' },
      ] },
    { name: 'items', label: 'Itemized Specifications', type: 'lineItems', required: true,
      itemSchema: [
        { name: 'name', label: 'Description', type: 'text', required: true },
        { name: 'part_number', label: 'Part # / Model', type: 'text' },
        { name: 'quantity', label: 'Quantity', type: 'number', required: true },
        { name: 'unit_of_measure', label: 'Unit', type: 'text' },
        { name: 'unit_cost', label: 'Unit Cost (UGX)', type: 'number' },
        { name: 'specifications', label: 'Specifications', type: 'text' },
      ] },
    { name: 'justification', label: 'Business Justification', type: 'textarea', required: true },
  ],
};
